import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { ERROR_MESSAGE_PARAM, SUCCESS_MESSAGE_PARAM } from "./notices";

interface UrlNotification {
  status: "error" | "success";
  message: string;
}

const toNotification = (
  errorMessage: string | null,
  successMessage: string | null,
): UrlNotification | null => {
  if (errorMessage) return { status: "error", message: errorMessage };
  if (successMessage) return { status: "success", message: successMessage };

  return null;
};

export const useNotificationSearchParam = (): UrlNotification | null => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const errorMessage = searchParams.get(ERROR_MESSAGE_PARAM);
  const successMessage = searchParams.get(SUCCESS_MESSAGE_PARAM);

  const [notification, setNotification] = useState(() =>
    toNotification(errorMessage, successMessage),
  );

  useEffect(() => {
    const next = toNotification(errorMessage, successMessage);
    if (!next) return;

    setNotification(next);

    /**
     * Strip from the live URL, not this render's location: a `<Navigate>`
     * committed with this render (e.g. RedirectToSettings) may have already
     * moved on, and replacing with this render's path would send the user back.
     */
    const url = new URL(window.location.href);
    if (
      !url.searchParams.has(ERROR_MESSAGE_PARAM) &&
      !url.searchParams.has(SUCCESS_MESSAGE_PARAM)
    )
      return;

    url.searchParams.delete(ERROR_MESSAGE_PARAM);
    url.searchParams.delete(SUCCESS_MESSAGE_PARAM);
    navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
  }, [errorMessage, successMessage, navigate]);

  return notification;
};
